/**
 * useCvGeneration
 *
 * Owns the "generate an ATS-tailored CV" flow:
 * - atsSectionConfig  : per-section mode (conserver / modifier / modifier_description)
 * - atsAdaptationLevel: per-section adaptation level (fidele / modere / creatif)
 * - generate state    : isGenerating / generatedCvData / changesSummary / warnings
 * - runGenerate       : POST /api/ai?route=ats-optimize&action=generate
 * - applyPresetAts    : set both config maps to a named preset
 * - applyGenerated    : clone the generated CV into a new version and
 *                       switch the active template to it
 *
 * Persistence of atsSectionConfig / atsAdaptationLevel is keyed on
 * activeTemplate, matching the previous inline App.tsx behavior.
 *
 * The hook takes CV state setters (from useCvState) as params so that
 * applyGenerated can create the new version without cross-hook coupling.
 */

import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import type { CVData, ParsedJobOffer } from '../types';
import type {
  AtsSectionConfig,
  AdaptationLevelMap,
  AdaptationLevel,
} from '../lib/ai-types';
import { parseJsonResponse } from '../lib/fetch-utils';
import { cloneCvData, makeTemplateId } from '../lib/cv-utils';
import type { TemplateMetaItem } from './useCvState';

const PRESET_SECTION_CONFIG: Record<string, AtsSectionConfig> = {
  conservateur: {
    formations: 'conserver', langues: 'conserver', passions: 'conserver',
    skills: 'conserver', profil: 'modifier', experiences: 'conserver',
  },
  standard: {
    formations: 'conserver', langues: 'conserver', passions: 'conserver',
    skills: 'modifier', profil: 'modifier', experiences: 'modifier_description',
  },
  inventif: {
    formations: 'modifier', langues: 'modifier', passions: 'modifier',
    skills: 'modifier', profil: 'modifier', experiences: 'modifier_description',
  },
};

const PRESET_ADAPTATION_LEVEL: Record<string, AdaptationLevelMap> = {
  conservateur: { profil: 'fidele' as AdaptationLevel },
  standard: { profil: 'modere' as AdaptationLevel, skills: 'modere' as AdaptationLevel, experiences: 'modere' as AdaptationLevel },
  inventif: { profil: 'creatif' as AdaptationLevel, skills: 'modere' as AdaptationLevel, experiences: 'modere' as AdaptationLevel, formations: 'modere' as AdaptationLevel, langues: 'modere' as AdaptationLevel, passions: 'modere' as AdaptationLevel },
};

const DEFAULT_SECTION_CONFIG: AtsSectionConfig = {
  formations: 'conserver',
  langues: 'conserver',
  passions: 'conserver',
  skills: 'conserver',
  profil: 'conserver',
  experiences: 'conserver',
};

function initAtsSectionConfig(defaultPreset?: AtsPreset): AtsSectionConfig {
  const active = localStorage.getItem('cv_active_template') || 'classique';
  const saved = localStorage.getItem(`cv_ats_section_config_${active}`);
  if (saved) {
    try {
      const parsed = JSON.parse(saved) as Partial<AtsSectionConfig>;
      return { ...DEFAULT_SECTION_CONFIG, ...parsed };
    } catch (e) {
      console.error('Failed to parse ats section config', e);
    }
  }
  if (defaultPreset && PRESET_SECTION_CONFIG[defaultPreset]) {
    return { ...PRESET_SECTION_CONFIG[defaultPreset] };
  }
  return { ...DEFAULT_SECTION_CONFIG };
}

function initAtsAdaptationLevel(defaultPreset?: AtsPreset): AdaptationLevelMap {
  const active = localStorage.getItem('cv_active_template') || 'classique';
  const saved = localStorage.getItem(`cv_ats_adaptation_level_${active}`);
  if (saved) {
    try {
      return JSON.parse(saved) as AdaptationLevelMap;
    } catch (e) {
      console.error('Failed to parse ats adaptation level', e);
    }
  }
  if (defaultPreset && PRESET_ADAPTATION_LEVEL[defaultPreset]) {
    return { ...PRESET_ADAPTATION_LEVEL[defaultPreset] };
  }
  return {};
}

const MISSING_KEY_HINT =
  '\n\nSur VPS : systemctl restart atsmycv. En local : .env (OPENROUTER_API_KEY ou MINIMAX_API_KEY).';

function formatGenerateError(e: unknown): string {
  const msg = e instanceof Error ? e.message : 'Erreur';
  const mentionsKey =
    msg.includes('MINIMAX_API_KEY') ||
    msg.includes('OPENROUTER_API_KEY') ||
    msg.includes('non configurée');
  const mentionsMissing = msg.includes('not set') || msg.includes('Définissez');
  return mentionsKey && mentionsMissing ? msg + MISSING_KEY_HINT : msg;
}

export type AtsPreset = 'conservateur' | 'standard' | 'inventif';

export interface UseCvGenerationParams {
  activeTemplate: string;
  cvData: CVData;
  effectiveJobDescription: string;
  getResumeText: (cv: CVData) => string;

  // For applyGenerated :
  parsedJobOffer: ParsedJobOffer | null;
  jobTitle: string;
  templateMeta: TemplateMetaItem[];
  setAllTemplatesData: Dispatch<SetStateAction<Record<string, CVData>>>;
  setTemplateMeta: Dispatch<SetStateAction<TemplateMetaItem[]>>;
  setActiveTemplate: Dispatch<SetStateAction<string>>;

  /** Default ATS preset from global settings (used when no per-template config is saved) */
  defaultAtsPreset?: AtsPreset;
}

export interface UseCvGenerationReturn {
  // Config
  atsSectionConfig: AtsSectionConfig;
  setAtsSectionConfig: Dispatch<SetStateAction<AtsSectionConfig>>;
  atsAdaptationLevel: AdaptationLevelMap;
  setAtsAdaptationLevel: Dispatch<SetStateAction<AdaptationLevelMap>>;
  applyPresetAts: (preset: AtsPreset) => void;

  // Generate state
  isGenerating: boolean;
  generatedCvData: CVData | null;
  generateChangesSummary: string | null;
  generateWarnings: string[] | null;

  // Actions
  runGenerate: () => Promise<void>;
  applyGenerated: () => void;
  /** Apply a partially-merged CV (from the diff viewer) as a new version. */
  applyGeneratedPartial: (merged: CVData) => void;
}

export function useCvGeneration(params: UseCvGenerationParams): UseCvGenerationReturn {
  const {
    activeTemplate,
    cvData,
    effectiveJobDescription,
    getResumeText,
    parsedJobOffer,
    jobTitle,
    templateMeta,
    setAllTemplatesData,
    setTemplateMeta,
    setActiveTemplate,
    defaultAtsPreset,
  } = params;

  const [atsSectionConfig, setAtsSectionConfig] =
    useState<AtsSectionConfig>(() => initAtsSectionConfig(defaultAtsPreset));
  const [atsAdaptationLevel, setAtsAdaptationLevel] =
    useState<AdaptationLevelMap>(() => initAtsAdaptationLevel(defaultAtsPreset));

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCvData, setGeneratedCvData] = useState<CVData | null>(null);
  const [generateChangesSummary, setGenerateChangesSummary] = useState<string | null>(null);
  const [generateWarnings, setGenerateWarnings] = useState<string[] | null>(null);

  // ---- persistence (keyed on activeTemplate) ----
  useEffect(() => {
    try {
      localStorage.setItem(
        `cv_ats_section_config_${activeTemplate}`,
        JSON.stringify(atsSectionConfig),
      );
    } catch {
      /* ignore */
    }
  }, [atsSectionConfig, activeTemplate]);

  useEffect(() => {
    try {
      localStorage.setItem(
        `cv_ats_adaptation_level_${activeTemplate}`,
        JSON.stringify(atsAdaptationLevel),
      );
    } catch {
      /* ignore */
    }
  }, [atsAdaptationLevel, activeTemplate]);

  // ---- presets ----
  const applyPresetAts = useCallback((preset: AtsPreset) => {
    if (preset === 'conservateur') {
      setAtsSectionConfig({
        formations: 'conserver',
        langues: 'conserver',
        passions: 'conserver',
        skills: 'conserver',
        profil: 'modifier',
        experiences: 'conserver',
      });
      setAtsAdaptationLevel({ profil: 'fidele' as AdaptationLevel });
    } else if (preset === 'standard') {
      setAtsSectionConfig({
        formations: 'conserver',
        langues: 'conserver',
        passions: 'conserver',
        skills: 'modifier',
        profil: 'modifier',
        experiences: 'modifier_description',
      });
      setAtsAdaptationLevel({
        profil: 'modere' as AdaptationLevel,
        skills: 'modere' as AdaptationLevel,
        experiences: 'modere' as AdaptationLevel,
      });
    } else if (preset === 'inventif') {
      setAtsSectionConfig({
        formations: 'modifier',
        langues: 'modifier',
        passions: 'modifier',
        skills: 'modifier',
        profil: 'modifier',
        experiences: 'modifier_description',
      });
      setAtsAdaptationLevel({
        profil: 'creatif' as AdaptationLevel,
        skills: 'modere' as AdaptationLevel,
        experiences: 'modere' as AdaptationLevel,
        formations: 'modere' as AdaptationLevel,
        langues: 'modere' as AdaptationLevel,
        passions: 'modere' as AdaptationLevel,
      });
    }
  }, []);

  // ---- generate ----
  const runGenerate = useCallback(async () => {
    if (!effectiveJobDescription?.trim()) {
      setGenerateChangesSummary(
        'Collez ou saisissez une offre dans "Offre d\'emploi cible" puis réessayez.',
      );
      setGeneratedCvData(null);
      return;
    }
    setIsGenerating(true);
    setGeneratedCvData(null);
    setGenerateChangesSummary(null);
    setGenerateWarnings(null);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          route: 'ats-optimize',
          action: 'generate',
          resumeText: getResumeText(cvData),
          jobDescription: effectiveJobDescription,
          cvData,
          sectionConfig: atsSectionConfig,
          adaptationLevel: atsAdaptationLevel,
        }),
      });
      const json = await parseJsonResponse<{
        error?: string;
        data?: CVData;
        changesSummary?: string;
        warnings?: string[];
      }>(res);
      if (!res.ok) throw new Error(json.error || 'Erreur génération CV adapté');
      setGeneratedCvData(json.data ?? null);
      setGenerateChangesSummary(json.changesSummary ?? null);
      setGenerateWarnings(json.warnings ?? null);
    } catch (e) {
      console.error('[Génération CV adapté] Error:', e);
      setGenerateChangesSummary(formatGenerateError(e));
      setGeneratedCvData(null);
      setGenerateWarnings(null);
    } finally {
      setIsGenerating(false);
    }
  }, [cvData, effectiveJobDescription, atsSectionConfig, atsAdaptationLevel, getResumeText]);

  // ---- apply generated to a new CV version ----
  const applyGenerated = useCallback(() => {
    if (!generatedCvData) return;
    const adapted = cloneCvData(generatedCvData);
    // Preserve the user's avatar (AI generation may drop or change it).
    if (cvData.personalInfo.avatar) {
      adapted.personalInfo = {
        ...adapted.personalInfo,
        avatar: cvData.personalInfo.avatar,
      };
    }
    const existingIds = templateMeta.map((tpl) => tpl.id);
    const baseName = (
      parsedJobOffer?.titre ||
      jobTitle?.trim() ||
      'Nouvelle version adaptée'
    ).slice(0, 60);
    const name = baseName.length > 60 ? baseName.slice(0, 57) + '...' : baseName;
    const newId = makeTemplateId(name, existingIds);
    setAllTemplatesData((prev) => ({ ...prev, [newId]: adapted }));
    setTemplateMeta((prev) => [...prev, { id: newId, name }]);
    setActiveTemplate(newId);
    setGeneratedCvData(null);
    setGenerateChangesSummary(null);
    setGenerateWarnings(null);
  }, [
    generatedCvData,
    cvData.personalInfo.avatar,
    templateMeta,
    parsedJobOffer,
    jobTitle,
    setAllTemplatesData,
    setTemplateMeta,
    setActiveTemplate,
  ]);

  const applyGeneratedPartial = useCallback(
    (merged: CVData) => {
      const adapted = cloneCvData(merged);
      if (cvData.personalInfo.avatar) {
        adapted.personalInfo = { ...adapted.personalInfo, avatar: cvData.personalInfo.avatar };
      }
      const existingIds = templateMeta.map((tpl) => tpl.id);
      const baseName = (parsedJobOffer?.titre || jobTitle?.trim() || 'Version adaptée').slice(0, 60);
      const name = baseName.length > 60 ? baseName.slice(0, 57) + '...' : baseName;
      const newId = makeTemplateId(name, existingIds);
      setAllTemplatesData((prev) => ({ ...prev, [newId]: adapted }));
      setTemplateMeta((prev) => [...prev, { id: newId, name }]);
      setActiveTemplate(newId);
      setGeneratedCvData(null);
      setGenerateChangesSummary(null);
      setGenerateWarnings(null);
    },
    [cvData.personalInfo.avatar, templateMeta, parsedJobOffer, jobTitle, setAllTemplatesData, setTemplateMeta, setActiveTemplate],
  );

  return {
    atsSectionConfig,
    setAtsSectionConfig,
    atsAdaptationLevel,
    setAtsAdaptationLevel,
    applyPresetAts,
    isGenerating,
    generatedCvData,
    generateChangesSummary,
    generateWarnings,
    runGenerate,
    applyGenerated,
    applyGeneratedPartial,
  };
}
